import { useState, useEffect, useCallback, useMemo } from 'react';
import type { QuickAddCombo } from '../types';
import { quickAddCombosService } from '../lib/database';
import { getDataSource } from '../lib/config';

interface UseQuickAddCombosReturn {
  combos: QuickAddCombo[];
  loading: boolean;
  error: string | null;
  create: (combo: Omit<QuickAddCombo, 'id'>) => Promise<QuickAddCombo>;
  update: (id: number, updates: Partial<QuickAddCombo>) => Promise<QuickAddCombo>;
  remove: (id: number) => Promise<void>;
  refresh: () => Promise<void>;
}

export const useQuickAddCombos = (enabled = true): UseQuickAddCombosReturn => {
  const [combos, setCombos] = useState<QuickAddCombo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCombos = useCallback(async () => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      setCombos([]); // No mock combos - use database
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await quickAddCombosService.getAll();
      setCombos(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load quick add combos';
      setError(errorMessage);
      console.error('Error fetching quick add combos:', err);
      setCombos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchCombos();
  }, [fetchCombos, enabled]);

  const create = useCallback(async (combo: Omit<QuickAddCombo, 'id'>): Promise<QuickAddCombo> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      throw new Error('Quick add combos must be configured in Firestore');
    }

    setError(null);
    try {
      const newCombo = await quickAddCombosService.create(combo);
      setCombos(prev => [...prev, newCombo]);
      return newCombo;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create quick add combo';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const update = useCallback(async (id: number, updates: Partial<QuickAddCombo>): Promise<QuickAddCombo> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      throw new Error('Quick add combos must be configured in Firestore');
    }

    setError(null);
    try {
      const updatedCombo = await quickAddCombosService.update(id, updates);
      setCombos(prev => prev.map(c => c.id === id ? updatedCombo : c));
      return updatedCombo;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update quick add combo';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const remove = useCallback(async (id: number): Promise<void> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      throw new Error('Quick add combos must be configured in Firestore');
    }

    setError(null);
    try {
      await quickAddCombosService.delete(id);
      setCombos(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete quick add combo';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchCombos();
  }, [fetchCombos]);

  return { combos, loading, error, create, update, remove, refresh };
};

